import * as React from "react";
import { clsx } from "@plans/appkit";
import { AlertCircleIcon } from "@plans/icons/untitled-ui/AlertCircleIcon";
import { controlSizeClassMap } from "~/ui/constants";
import { useDOMConstraintValidation } from "~/ui/useDomConstraintValidation";
import { useResponsiveSize } from "~/ui/useResponsiveSize";
import {
	FormControl,
	formControlSuffixProps,
	getAriaErrorMessageIdForControl,
} from "./FormControl";
import type { FormControlProps } from "./FormControl";

type Type =
	| "text"
	| "password"
	| "email"
	| "number"
	| "integer"
	| "tel"
	| "url"
	| "search"
	| "date";

export type TextInputProps = Omit<
	React.ComponentPropsWithoutRef<"input">,
	"disabled" | "size" | "children" | "required" | "prefix"
> &
	FormControlProps & {
		/**
		 * The input element. A normal prop in React 19, so callers can pass it
		 * the same way they pass any other prop.
		 */
		ref?: React.Ref<HTMLInputElement | null>;
		/**
		 * Type of the input. Defaults to `text`.
		 */
		type?: Type;
		/**
		 * The size attribute defines the width of the <input>.
		 *
		 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/size
		 */
		htmlSize?: number;
		/**
		 * Applied after the size classes — caller classes win (twMerge). This is
		 * the supported escape hatch for feature-local restyling.
		 */
		inputClassName?: string;
		/**
		 * Optional className to apply to the input element shadow.
		 */
		inputShadowClassName?: string;
	};

export function TextInput({
	name,
	label,
	labelClassName,
	labelAction,
	className,
	inputClassName,
	inputShadowClassName,
	type: propsType = "text",
	inputMode: propsInputMode,
	step: propsStep,
	onWheelCapture: propsOnWheelCapture,
	isInvalid: propsIsInvalid,
	errorMessage: propsErrorMessage,
	min: propsMin,
	isDisabled,
	isRequired,
	size = "medium",
	hint,
	prefix,
	prefixIcon,
	suffix,
	suffixIcon,
	insetSuffix,
	tooltip,
	defaultValue,
	htmlSize,
	orientation,
	ref,
	...htmlProps
}: TextInputProps) {
	const sizeClassList = useResponsiveSize(size, controlSizeClassMap);
	const [validationRef, contraintErrorMessage, constraintIsInvalid] =
		useDOMConstraintValidation();
	const isInvalid = propsIsInvalid || constraintIsInvalid;
	const errorMessage = propsErrorMessage ?? contraintErrorMessage;

	let type = propsType;
	let inputMode = propsInputMode;
	let onWheelCapture = propsOnWheelCapture;
	let step = propsStep;
	let min = propsMin;

	switch (propsType) {
		case "integer": {
			type = "number";
			step = "1";
			// No default floor: the caller declares its own `min` — the TI forms
			// derive one from their Zod schema via
			// `useDomValidationConstraintsFromZodSchema`, and SDUI forms get one
			// from the packet. Defaulting to 0 here silently replaced a declared
			// bound with a weaker one.
			min = propsMin;
			inputMode = "numeric";
			onWheelCapture = (event) => {
				event.currentTarget.blur();
				propsOnWheelCapture?.(event);
			};
			break;
		}
		case "number": {
			type = "number";
			step = propsStep ?? "any";
			inputMode = "decimal";
			onWheelCapture = (event) => {
				event.currentTarget.blur();
				propsOnWheelCapture?.(event);
			};
			break;
		}
	}

	const suffixProps = formControlSuffixProps(suffix, insetSuffix);

	return (
		<FormControl
			name={name}
			className={className}
			label={label}
			labelClassName={labelClassName}
			labelHtmlFor={name}
			labelAction={labelAction}
			errorMessage={errorMessage}
			isInvalid={isInvalid}
			isDisabled={isDisabled}
			isRequired={isRequired}
			tooltip={tooltip}
			hint={hint}
			size={size}
			prefix={prefix}
			prefixIcon={prefixIcon}
			{...suffixProps}
			orientation={orientation}
			suffixIcon={
				suffixIcon ??
				(isInvalid && (
					<AlertCircleIcon className="text-accent-error pointer-events-none h-4 w-4" />
				))
			}
		>
			<input
				data-cy={name}
				ref={(element) => {
					validationRef(element);
					if (typeof ref === "function") {
						ref(element);
					} else if (ref) {
						ref.current = element;
					}
				}}
				id={name}
				name={name}
				type={type}
				step={step}
				min={min}
				inputMode={inputMode}
				disabled={isDisabled}
				required={isRequired}
				onWheelCapture={onWheelCapture}
				defaultValue={defaultValue}
				size={htmlSize}
				aria-invalid={isInvalid}
				aria-errormessage={getAriaErrorMessageIdForControl(name)}
				placeholder="—"
				className={clsx(
					"w-full appearance-none rounded-md border",
					"global-focus global-no-spinner peer !outline-4 focus-visible:outline-blue-100",
					"bg-background-default disabled:bg-background-soft placeholder-gray-400",
					isInvalid
						? "border-accent-error"
						: "border-background-medium focus-visible:border-background-soft [&:not(:disabled):not(:focus-visible)]:hover:border-background-contrast",
					...sizeClassList,
					inputClassName,
				)}
				{...htmlProps}
			/>
			<span
				role="presentation"
				className={clsx(
					"duration-50 pointer-events-none absolute inset-0 select-none rounded-md opacity-0 outline-1 outline-blue-300 transition-[outline] peer-focus-visible:opacity-100",
					inputShadowClassName,
				)}
			/>
		</FormControl>
	);
}
